import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import api from '../../services/api';
import { getApiErrorInfo } from '../../services/api';
import tenantService from '../../services/tenantService';
import { NO_SUBSCRIPTION_CONFIG, subscriptionChipSx, type SubscriptionTone } from '../../utils/subscriptionStatus';

interface Tenant {
  id: string;
  name: string;
  logoUrl: string;
  isActive: boolean;
  createdAt: string;
  subscriptionStatus?: string;
  userCount: number;
}

type SetupStatus = 'active' | 'no-users' | 'pending-payment' | 'no-subscription' | 'inactive';

function getSetupStatus(tenant: Tenant): SetupStatus {
  if (!tenant.isActive) return 'inactive';
  const sub = tenant.subscriptionStatus;
  if (!sub) return 'no-subscription';
  if (sub === 'active' || sub === 'non_renewing' || sub === 'in_trial') {
    return tenant.userCount === 0 ? 'no-users' : 'active';
  }
  return 'pending-payment';
}

const STATUS_CONFIG: Record<SetupStatus, { label: string; tone: SubscriptionTone; icon?: React.ReactElement; tooltip: string }> = {
  active:            { label: 'Active',           tone: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, tooltip: 'Subscription active and users enrolled' },
  'no-users':        { label: 'No Users',         tone: 'warning', icon: <PeopleAltIcon  sx={{ fontSize: 14 }} />, tooltip: 'Subscription active but no users have been added yet' },
  'pending-payment': { label: 'Pending Payment',  tone: 'warning', icon: <WarningIcon    sx={{ fontSize: 14 }} />, tooltip: 'Subscription exists but is not yet active; payment may be pending' },
  'no-subscription': { ...NO_SUBSCRIPTION_CONFIG, tooltip: 'No subscription found; tenant has not completed billing setup' },
  inactive:          { label: 'Inactive',         tone: 'neutral',                                                  tooltip: 'Tenant is marked inactive' },
};

export default function Tenants() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      console.log('Fetching tenants from:', '/api/tenants');
      const response = await api.get('/api/tenants');
      console.log('Tenants response:', response.data);
      setTenants(response.data);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('Error fetching tenants:', error);
      console.error('Error response:', apiError.data);
      console.error('Error status:', apiError.status);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        // Update tenant data
        await tenantService.updateTenant(editingTenant.id, formData);
        
        // Upload logo if a new file was selected
        if (logoFile) {
          await tenantService.uploadTenantLogo(editingTenant.id, logoFile);
        }
      } else {
        // Create new tenant
        const { id } = await tenantService.createTenant(formData);
        
        // Upload logo if provided
        if (logoFile) {
          await tenantService.uploadTenantLogo(id, logoFile);
        }
      }
      fetchTenants();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving tenant:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    
    try {
      await tenantService.deleteTenant(id);
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
    }
  };

  const handleEdit = async (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      isActive: tenant.isActive,
    });
    
    // Load logo preview if exists
    if (tenant.id) {
      try {
        const logoUrl = await tenantService.getTenantLogo(tenant.id);
        setLogoPreview(logoUrl);
      } catch {
        console.log('No logo found for tenant');
        setLogoPreview(null);
      }
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTenant(null);
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({
      name: '',
      isActive: true,
    });
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>

        {/* Search and Add Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            fullWidth
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowModal(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Tenant
          </Button>
        </Box>

        {/* Tenants Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Tenant Status</TableCell>
                  <TableCell>Setup Status</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 12, color: 'text.secondary' }}>
                      No tenants found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTenants.map((tenant) => (
                    <TableRow
                      key={tenant.id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Box
                          component={Link}
                          to={`/app/system-admin/tenants/${tenant.id}`}
                          sx={{ display: 'flex', alignItems: 'center', gap: 2, textDecoration: 'none', color: 'inherit', '&:hover': { color: 'primary.main' } }}
                        >
                          {tenant.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tenant.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={tenant.isActive ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = getSetupStatus(tenant);
                          const cfg = STATUS_CONFIG[s];
                          return (
                            <Tooltip title={cfg.tooltip} arrow>
                              <Chip
                                label={cfg.label}
                                size="small"
                                {...(cfg.icon ? { icon: cfg.icon } : {})}
                                sx={[subscriptionChipSx(cfg.tone), { cursor: 'default' }]}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {tenant.userCount}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {new Date(tenant.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            onClick={() => handleEdit(tenant)}
                            sx={{ color: 'primary.main' }}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(tenant.id)}
                            sx={{ color: 'error.main' }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                          <Button
                            component={Link}
                            to={`/app/tenant-admin/users?tenantId=${tenant.id}`}
                            startIcon={<PersonAddIcon />}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ ml: 1 }}
                          >
                            Manage Users
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Modal */}
        <Dialog
          open={showModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              backgroundImage: 'none',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
            {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Tenant Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />

              {/* Logo Upload Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Logo
                </Typography>

                {logoPreview && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{
                        height: '80px',
                        width: 'auto',
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'rgba(0,0,0,0.12)',
                        borderRadius: '8px',
                        padding: '8px',
                      }}
                    />
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Choose Logo File
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  PNG, JPG, SVG up to 5MB
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={handleCloseModal} variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                {editingTenant ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
  );
}
